import React, { useContext, useMemo, useRef, useState } from 'react';
import { arrayOf, bool, func, number, oneOfType, shape, string } from 'prop-types';
import { Box, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import * as R from 'ramda';

// Utils
import { formatFEIN } from 'feature/loading-management/utils/format';
import { AuthContext } from 'authentication/AuthContext';
import { useSetLoadingTitle } from 'hooks';

// Components
import { cardsListStyles, cardsPanelContainerStyles } from 'feature/loading-management/utils/const';
import { NoAuthorizationsAvailable } from 'feature/loading-management/shared-components';
import ExplicitConfirmationDialog from 'common/dialog-explicit-confirmation/DialogExplicitConfirmation';
import CardSkeletonList from '../../card-skeleton/CardSkeletonList';
import CardsPanelHeader from './cards-panel-header/CardsPanelHeader';
import CardsList from './cards-list/CardsList';
import FiltersContainer from './filter-popover/FilterContainer';
import FilterIdentifierPanel from '../../filters/filter-identifier-panel/FilterIdentifierPanel.connector';

const initDialogState = {
  carrierName: undefined,
  dialogOpen: false,
  explicit: false,
  id: undefined,
  locationName: undefined,
};

const FilterIdentifierContainer = () => ({
  alignItems: 'center',
  display: 'flex',
  flexWrap: 'wrap',
  margin: `6px 0 0 16px`,
  width: '100%',
});

const CardsPanel = ({
  carrierAuthorizationsCards,
  carrierAuthorizationsPending,
  carrierAuthorizationsCardsLength,
  currentSelection,
  filtersOpen,
  headerSubtitle,
  filterIdentifierCount,
  onEdit,
  removeCarrierAuthorization,
  resetCarrierAuthWizardData,
  setFiltersOpen,
  viewBy,
}) => {
  const lgUp = useMediaQuery(theme => theme.breakpoints.up('lg'), { noSsr: true });
  const { currentAffiliate } = useContext(AuthContext);
  const filtersRef = useRef();
  const [dialogDetails, setDialogDetails] = useState(initDialogState);
  const theme = useTheme();

  const handleRemove = (id, nameShort) => {
    const carrierName = R.propOr(nameShort, 'carrierName')(currentSelection);
    const locationName = R.propOr(nameShort, 'nameShort')(currentSelection);
    setDialogDetails({ carrierName, dialogOpen: true, explicit: true, id, locationName });
  };

  const handleConfirm = () => {
    const { carrierName, locationName, id } = dialogDetails;
    removeCarrierAuthorization(id, carrierName, locationName, currentAffiliate);
    setDialogDetails(initDialogState);
  };

  const warningList = useMemo(() => {
    const { carrierName, locationName } = dialogDetails;
    return [
      `${carrierName} will be removed from all Customer Authorizations associated with ${locationName}`,
      'This action cannot be undone once executed.',
    ];
  }, [dialogDetails]);

  const title = useSetLoadingTitle(carrierAuthorizationsPending, currentSelection);

  const renderCards = () => {
    if (carrierAuthorizationsPending) return <CardSkeletonList viewBy={viewBy} />;
    if (!carrierAuthorizationsCardsLength) return <NoAuthorizationsAvailable />;
    return (
      <CardsList
        cards={carrierAuthorizationsCards}
        handleRemove={handleRemove}
        onEdit={onEdit}
        viewBy={viewBy}
      />
    );
  };

  const renderSubtitle = () => {
    if (!carrierAuthorizationsPending && viewBy === 'carrier' && headerSubtitle)
      return `FEIN ${formatFEIN(headerSubtitle)}`;
    return null;
  };

  // doing something a little different here because there are no list tabs and the header has a subtitle
  return (
    <Box {...cardsPanelContainerStyles}>
      {lgUp && (
        <CardsPanelHeader
          carrierAuthorizationsCardsLength={carrierAuthorizationsCardsLength}
          filterIdentifierCount={filterIdentifierCount}
          filtersOpen={filtersOpen}
          filtersRef={filtersRef}
          pending={carrierAuthorizationsPending}
          resetCarrierAuthWizardData={resetCarrierAuthWizardData}
          setFiltersOpen={setFiltersOpen}
          subtitle={renderSubtitle()}
          title={title}
        />
      )}
      {lgUp && <FilterIdentifierPanel sx={FilterIdentifierContainer(theme)} />}
      <Box {...cardsListStyles} pb={0}>
        {renderCards()}
      </Box>
      <FiltersContainer
        filtersRef={filtersRef}
        handleClose={() => setFiltersOpen(false)}
        lgUp={lgUp}
        open={filtersOpen}
        viewBy={viewBy}
      />
      {dialogDetails.dialogOpen && (
        <ExplicitConfirmationDialog
          confirmationMessage="If you still want to remove this Carrier Authorization, enter the name below and click Confirm."
          confirmationValue={dialogDetails.carrierName}
          dialogOpen={dialogDetails.dialogOpen}
          handleCloseDialog={() => setDialogDetails(initDialogState)}
          onConfirm={handleConfirm}
          title="Remove Carrier Authorization"
          warningList={warningList}
        />
      )}
    </Box>
  );
};

CardsPanel.propTypes = {
  carrierAuthorizationsCards: oneOfType([arrayOf(shape({})), shape({})]).isRequired,
  carrierAuthorizationsCardsLength: number.isRequired,
  carrierAuthorizationsPending: bool.isRequired,
  currentSelection: shape({}).isRequired,
  filterIdentifierCount: number.isRequired,
  filtersOpen: bool.isRequired,
  headerSubtitle: string,
  onEdit: func.isRequired,
  removeCarrierAuthorization: func.isRequired,
  resetCarrierAuthWizardData: func.isRequired,
  setFiltersOpen: func.isRequired,
  viewBy: string,
};

CardsPanel.defaultProps = {
  headerSubtitle: '',
  viewBy: '',
};

export default CardsPanel;
